/* eslint-disable no-console */
import React from 'react';

export interface UseToggleChainResult {
    /**
     * Если включено - HTMLElement, если выключено - undefined
     */
    triggerElement: HTMLElement | undefined;

    /**
     * Включить triggerElement
     * Может использоваться как обработчик onMouseEnter или onClick
     *
     * @param e.currentTarget запишется в triggerElement, выполнится e.preventDefault()
     */
    show(e: React.MouseEvent<HTMLElement>): void;

    /**
     * Выключить triggerElement
     * Может использоваться как обработчик onMouseLeave или колбэк закрытия модалки/тултипа
     *
     * @param e если задан, выполнится e.preventDefault();
     */
    hide(e?: React.MouseEvent<HTMLElement>): void;

    /**
     * Включить или выключить без задержки, даже если она задана в параметрах.
     * Используется для связи родительского и дочернего useToggleChain, что б дочерний мог перехватить владение triggerElement,
     * через lock сообщив родительскому, что элемент выключать не надо.
     *
     */
    lock(isLocked: boolean): void;
}

/**
 * Отслеживает hover на элементе.
 *
 * Например, курсор на ссылке через useToggleChain включил всплывашку, которая через свой инстанс useToggleChain отслеживает hover на самой себе
 * и через lock сообщает инстансу useToggleChain у ссылки, о том, что всплывашку нельзя удалять, хоть фокус уже и не на ссылке.
 *
 * @example
 * ```tsx
 * function Link() {
 *     const spy = useToggleChain({ enterTimeout: 500, leaveTimeout: 500 });
 *     return <>
 *         <a onMouseEnter={spy.show} onMouseLeave={spy.hide}>link text</a>
 *         {spy.triggerElement && <Popup lock={spy.lock} triggerElement={spy.triggerElement} />}
 *     </>
 * }
 *
 * function Popup({lock, triggerElement}) {
 *     const spy = useToggleChain({ lock, leaveTimeout: 500 });
 *     return <div onMouseEnter={spy.show} onMouseLeave={spy.hide}>Text</div>
 * }
 * ```
 */
export function useToggleChain({
    enterTimeout = 0,
    leaveTimeout = 0,
    lock: lockParent,
    debugLabel
}: {
    /**
     * Если задан, в консоль будут приходить отладочные сообщения
     */
    debugLabel?: string;

    /**
     * Для связывания дочернего toggle chain с родительским.
     */
    lock?(isLocked: boolean): void;

    /**
     * Задержка включения, по-умолчанию 0
     */
    enterTimeout?: number;

    /**
     * Задержка выключения, по-умолчанию 0
     */
    leaveTimeout?: number;
} = {}): UseToggleChainResult {
    const [ , setCount ] = React.useState(0);
    const triggerElementRef = React.useRef<HTMLElement>();
    const countRef = React.useRef(0);
    // eslint-disable-next-line no-undef
    const timerRef = React.useRef<NodeJS.Timer>();

    const setEnabled = React.useCallback((
        isEnabled: boolean,
        e?: React.MouseEvent<HTMLElement>,
        timeout = isEnabled ? enterTimeout : leaveTimeout,
    ) => {
        e?.preventDefault();
        timerRef.current && clearTimeout(timerRef.current);
        timerRef.current = undefined;

        if (isEnabled) {
            countRef.current++;
            if (debugLabel) console.log(debugLabel, 'inc:', countRef.current);
            if (countRef.current > 1 || triggerElementRef.current) return;
        } else {
            countRef.current = Math.max(0, countRef.current - 1);
            if (debugLabel) console.log(debugLabel, 'dec:', countRef.current);
            if (countRef.current !== 0) return;
        }

        const nextElement = isEnabled ? (e?.currentTarget ?? undefined) : undefined;

        if (isEnabled && ! nextElement) throw new Error('e.currentTarget is empty');

        const update = () => {
            triggerElementRef.current = nextElement;
            setCount(p => p + 1);
            lockParent?.(isEnabled);
            if (debugLabel) {
                console.log(debugLabel, isEnabled ? 'show' : 'hide');
            }
        };

        if (! timeout) return update();

        timerRef.current = setTimeout(update, timeout);
    }, [ enterTimeout, leaveTimeout, lockParent ]);

    const show = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        setEnabled(true, e);
    }, [ setEnabled ]);

    const hide = React.useCallback((e?: React.MouseEvent<HTMLElement>) => {
        setEnabled(false, e);
    }, [ setEnabled ]);

    const lock = React.useCallback((isLocked: boolean) => {
        setEnabled(isLocked, undefined, 0);
    }, [ setEnabled ]);

    return {
        triggerElement: triggerElementRef.current,
        show,
        hide,
        lock
    };
}
